import React, { useRef, useState } from 'react';
import {
  IonButton,
  IonDatetime,
  IonDatetimeButton,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonPopover,
  useIonRouter,
} from '@ionic/react';
import { checkmark, chevronExpandOutline, pencilOutline } from 'ionicons/icons';
import SectionCard from '@/components/SectionCard';
import { useUserContext } from '@/modules/user/useUserContext';
import { TopicLogic } from '@/modules/topic/logic';
import type { BylineChoice } from '@/modules/topic/types';
import {
  MAX_BYLINE_LENGTH,
  visuallyHiddenInputStyle,
} from '@/modules/topic/upsert/constants';
import type { TimezoneOption } from '@/modules/topic/timezone';
import ClearButton from '@/modules/topic/upsert/ClearButton';
import LocationRow from '@/modules/topic/upsert/LocationRow';
import TimezoneSelect from '@/modules/topic/upsert/TimezoneSelect';
import { useZoneFollowingLocation } from '@/modules/topic/upsert/useZoneFollowingLocation';
import styles from './EditableBylineDatelineBlock.module.css';

interface EditableBylineDatelineBlockProps {
  byline: string;
  setByline: (value: string) => void;
  location: string;
  setLocation: (value: string) => void;
  /**
   * The topic's start, which is the dateline's day and time: a wall-clock
   * `YYYY-MM-DDTHH:mm:ss` in `timezone`. Midnight reads as a day alone.
   */
  startTime: string;
  setStartTime: (value: string) => void;
  timezone: string;
  setTimezone: (value: string) => void;
  timezones: readonly TimezoneOption[];
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** The time a newly added dateline time starts at: now, to the 5 minutes. */
const nowToFiveMinutes = (): string => {
  const now = new Date();
  const minute = Math.floor(now.getMinutes() / 5) * 5;
  const hhmm = `${pad2(now.getHours())}:${pad2(minute)}`;
  // Midnight reads as a day alone, so a time added then starts a step later.
  return hhmm === '00:00' ? '00:05' : hhmm;
};

/**
 * The Byline and Dateline cards for a news story: who it is credited to, then
 * when and where it was filed.
 *
 * The byline is picked from a menu of the author's attribution names, a
 * custom name, or none. A custom name is typed into its own row, which
 * "Add custom name" opens and its ⓧ closes, leaving no byline. Both
 * attribution names otherwise live only on the author's device, so the card
 * says the byline is stored on our server, and links to where they are edited.
 *
 * The date is the topic's start, which every topic has, and defaults to
 * today. The date and time open Ionic's date and time pickers, which follow
 * each platform's own look. A time of day and its zone stay behind "Add a time" unless the story
 * has one; the ⓧ on the time removes it. The zone follows the location when it
 * names a place TopicLogic.timezoneForPlace knows, until the author picks a
 * zone themselves, and again once they remove the time.
 */
const EditableBylineDatelineBlock: React.FC<
  EditableBylineDatelineBlockProps
> = ({
  byline,
  setByline,
  location,
  setLocation,
  startTime,
  setStartTime,
  timezone,
  setTimezone,
  timezones,
}) => {
  const { familiarName, allowedName } = useUserContext();
  const router = useIonRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  // The menu pops up under the byline's name, as a standard pop-up menu.
  const menuButton = useRef<HTMLButtonElement>(null);
  // The custom-name row stays open while empty, until its ⓧ or a menu pick.
  const [customOpen, setCustomOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const { changeLocation, pickZone, releaseZone } = useZoneFollowingLocation(
    location,
    setLocation,
    setTimezone
  );

  const names = { familiarName, allowedName };
  const choice: BylineChoice = customOpen
    ? 'custom'
    : TopicLogic.bylineChoiceFor(byline, names);
  const showCustom = choice === 'custom';
  const nameOptions = [
    { value: 'familiar', name: familiarName, caption: 'Familiar Name' },
    { value: 'allowed', name: allowedName, caption: 'Allowed Name' },
  ] as const;
  const shown =
    nameOptions.find(option => option.value === choice) ??
    ({ name: choice === 'custom' ? 'Custom' : 'None', caption: '' } as const);

  const date = startTime.slice(0, 10);
  const time = startTime.slice(11, 16);
  const hasTime = time !== '' && time !== '00:00';
  const showTime = timeOpen || hasTime;
  const zoneOptions = timezones.some(tz => tz.value === timezone)
    ? timezones
    : [...timezones, { label: timezone, value: timezone }];

  const pick = (next: BylineChoice) => {
    setMenuOpen(false);
    if (next === 'custom') {
      setCustomOpen(true);
      setByline('');
      return;
    }
    setCustomOpen(false);
    if (next === 'familiar') setByline(familiarName ?? '');
    else if (next === 'allowed') setByline(allowedName ?? '');
    else setByline('');
  };

  const removeCustom = () => {
    setCustomOpen(false);
    setByline('');
  };

  const editProfileNames = () =>
    router.push(
      `/profile/steps/attribution?returnButtonLabel=${encodeURIComponent(
        'Back to your news'
      )}`,
      'forward',
      'push'
    );

  const setStart = (day: string, hhmm: string) =>
    setStartTime(`${day}T${hhmm || '00:00'}:00`);

  // The start always has a day, so an emptied date input changes nothing.
  const setDate = (value: string) => {
    if (value !== '') setStart(value, hasTime ? time : '');
  };

  // A picked time of 12:00 AM keeps its row open, though it reads as no time.
  const setTime = (hhmm: string) => {
    if (hhmm !== '') setStart(date, hhmm);
  };

  /** The `HH:mm` in a value ion-datetime reports, with or without a day. */
  const timeOf = (value: string) => value.match(/(\d{2}:\d{2})/)?.[1] ?? '';

  const addTime = () => {
    setTimeOpen(true);
    setStart(date, nowToFiveMinutes());
  };

  // Removing the time also lets go of a zone the author picked, so the zone
  // follows the location again.
  const removeTime = () => {
    setTimeOpen(false);
    setStart(date, '');
    releaseZone();
  };

  return (
    <div data-testid="byline-dateline-block">
      <SectionCard title="Byline" noBottomMargin>
        <IonItem lines="inset" className={styles.bylineRow}>
          <button
            ref={menuButton}
            type="button"
            className={styles.menuButton}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={`Byline: ${shown.name}`}
            data-testid="byline-menu-button"
            onClick={() => setMenuOpen(true)}
          >
            <span className={styles.menuName}>
              <span className={styles.ellipsis}>{shown.name}</span>
              <IonIcon icon={chevronExpandOutline} aria-hidden="true" />
            </span>
            {shown.caption && (
              <span className={styles.caption}>{shown.caption}</span>
            )}
          </button>
          <IonButton
            slot="end"
            fill="clear"
            className={styles.editNames}
            data-testid="byline-edit-profile-names"
            onClick={editProfileNames}
          >
            Edit profile names
          </IonButton>
        </IonItem>
        <IonPopover
          isOpen={menuOpen}
          event={{ target: menuButton.current }}
          onDidDismiss={() => setMenuOpen(false)}
          side="bottom"
          alignment="start"
          arrow={false}
          className={styles.menu}
        >
          <div role="menu" aria-label="Byline">
            {nameOptions
              .filter(option => option.name)
              .map(option => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  checked={choice === option.value}
                  onPick={pick}
                >
                  <span className={styles.menuItemText}>
                    <span>{option.name}</span>
                    <span className={styles.caption}>{option.caption}</span>
                  </span>
                </MenuItem>
              ))}
            <MenuItem
              value="custom"
              checked={choice === 'custom'}
              onPick={pick}
            >
              <span className={styles.menuItemText}>Custom…</span>
              <IonIcon icon={pencilOutline} aria-hidden="true" />
            </MenuItem>
            <div className={styles.menuGap} />
            <MenuItem value="none" checked={choice === 'none'} onPick={pick}>
              <span className={styles.menuItemText}>None</span>
            </MenuItem>
          </div>
        </IonPopover>
        {showCustom ? (
          <IonItem lines="none">
            <IonInput
              aria-label="Custom name"
              value={byline}
              onIonInput={e => setByline((e.detail.value as string) || '')}
              placeholder="Custom name"
              maxlength={MAX_BYLINE_LENGTH}
              autocapitalize="words"
              autocomplete="off"
              autocorrect="off"
              spellcheck={false}
              data-testid="byline-custom-input"
            />
            <ClearButton
              label="Remove custom name"
              testId="byline-custom-remove"
              onClick={removeCustom}
            />
          </IonItem>
        ) : (
          <IonItem
            button
            detail={false}
            lines="none"
            onClick={() => pick('custom')}
            data-testid="byline-add-custom"
          >
            <IonLabel color="primary">Add custom name</IonLabel>
          </IonItem>
        )}
      </SectionCard>
      <p className={styles.footnote} data-testid="byline-storage-notice">
        Stored on our server when this news is shared.
      </p>

      <SectionCard title="Dateline">
        <IonItem lines="inset">
          <IonLabel>Date</IonLabel>
          <IonDatetimeButton
            datetime="dateline-date"
            slot="end"
            data-testid="dateline-date-button"
          />
        </IonItem>

        {showTime ? (
          <>
            <IonItem lines="none">
              <IonLabel>Time</IonLabel>
              <IonDatetimeButton
                datetime="dateline-time"
                slot="end"
                data-testid="dateline-time-button"
              />
              <ClearButton
                label="Remove time"
                testId="dateline-time-remove"
                onClick={removeTime}
              />
            </IonItem>
            <TimezoneSelect
              timezone={timezone}
              setTimezone={pickZone}
              timezones={zoneOptions}
              className={styles.zoneRow}
              lines="inset"
              data-testid="dateline-timezone-select"
            />
          </>
        ) : (
          <IonItem
            button
            detail={false}
            lines="inset"
            onClick={addTime}
            data-testid="dateline-add-time-button"
          >
            <IonLabel color="primary">Add a time</IonLabel>
          </IonItem>
        )}

        <LocationRow
          location={location}
          onChange={changeLocation}
          onClear={() => setLocation('')}
          label="Location"
          testIdPrefix="dateline-location"
        />
      </SectionCard>

      <IonModal keepContentsMounted={true}>
        <IonDatetime
          id="dateline-date"
          presentation="date"
          value={date}
          onIonChange={e => {
            const v = e.detail.value;
            if (typeof v === 'string') setDate(v.slice(0, 10));
          }}
        />
      </IonModal>
      <IonModal keepContentsMounted={true}>
        <IonDatetime
          id="dateline-time"
          presentation="time"
          value={`${date}T${hasTime ? time : nowToFiveMinutes()}:00`}
          onIonChange={e => {
            const v = e.detail.value;
            if (typeof v === 'string') setTime(timeOf(v));
          }}
        />
      </IonModal>

      <input
        type="date"
        data-testid="dateline-date-input"
        value={date}
        onChange={e => setDate(e.target.value)}
        style={visuallyHiddenInputStyle}
        aria-hidden="true"
        tabIndex={-1}
      />
      <input
        type="time"
        data-testid="dateline-time-input"
        value={hasTime ? time : ''}
        onChange={e => setTime(e.target.value)}
        style={visuallyHiddenInputStyle}
        aria-hidden="true"
        tabIndex={-1}
      />
    </div>
  );
};

/** A byline menu choice, checked when it is the byline in use. */
const MenuItem: React.FC<{
  value: BylineChoice;
  checked: boolean;
  onPick: (value: BylineChoice) => void;
  children: React.ReactNode;
}> = ({ value, checked, onPick, children }) => (
  <button
    type="button"
    role="menuitemradio"
    aria-checked={checked}
    className={styles.menuItem}
    data-testid={`byline-menu-${value}`}
    onClick={() => onPick(value)}
  >
    <IonIcon
      icon={checkmark}
      aria-hidden="true"
      className={styles.check}
      style={{ visibility: checked ? 'visible' : 'hidden' }}
    />
    {children}
  </button>
);

export default EditableBylineDatelineBlock;
